import {gql, useApolloClient} from '@apollo/client';
import {Color} from '@benchling/nucleus-ui';
import {TextInput, Tooltip as MantineTooltip} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import type {CoreObject} from 'app-core/framework/CoreObjectConstants';
import {starItems, unstarItems} from 'app-core/platform/starrable/StarDAO';
import {assert} from 'Asserts';
import {ChatType, ChatMessageOriginType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {updateChatSession} from 'llm/chat-library/ChatDAO';
import {getDraftPreview, hasMeaningfulDraft, useChatDraftState} from 'llm/chat-library/ChatDraftState';
import {getChatTypeForNewAgentChat} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import ResultsPageLoader from 'nav/sidepanel/listing/ResultsPageLoader';
import SidepanelItem from 'nav/sidepanel/listing/SidepanelItem';
import {RealtimeMessageType} from 'realtime/Constants';
import useRealtimeChannelSubscription from 'realtime/useRealtimeChannelSubscription';
import {getRealtimeChannelNameForApiId} from 'realtime/util/RealtimeUtil';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import useFuzzyDate from 'ui-library/hooks/useFuzzyDate';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import OverflowMenu from 'ui-library/menus/OverflowMenu';
import Modal from 'ui-library/modals/Modal';
import {Container} from 'ui-library/NYC';
import {readableTime} from 'util/DateUtil';
import {assertNever} from 'util/Types';

import {ChatHistoryQuery, ChatHistoryQueryVariables} from './__generated/ChatHistoryQuery';
import {getChatNameRenderInfo} from './ChatNameUtil';
import type {SidepanelChat} from './ChatRouter';

interface ChatSidepanelItem {
  id: string | null;
  type: ChatType | null;
  name: string | null;
  initialOrigin?: ChatMessageOriginType | null;
  modifiedAt: string;
  star?: {
    id: string;
    isStarred: boolean;
  } | null;
}

// A chat initiated by a routine (rather than a person) is surfaced with a distinct icon.
function isRoutineInitiatedChat(item: ChatSidepanelItem): boolean {
  return item.initialOrigin === ChatMessageOriginType.ROUTINE;
}

// Tooltip describing what initiated the chat. Returns null when the origin is unknown (legacy
// sessions created before initialOrigin existed), so no tooltip is shown.
function getInitialOriginTooltip(item: ChatSidepanelItem): string | null {
  switch (item.initialOrigin) {
    case ChatMessageOriginType.ROUTINE:
      return 'Triggered by Background Agent';
    case ChatMessageOriginType.UI:
      return 'Triggered by User';
    case ChatMessageOriginType.API:
      return 'Triggered via API';
    case ChatMessageOriginType.LLM:
      return 'Triggered by Assistant';
    default:
      return null;
  }
}

interface ChatHistoryFetchState {
  prevTokens: Array<string>;
  currentToken: string;
  nextToken: string | null;
  items: Array<ChatHistoryQuery['chatSessionListing']['items'][number]>;
  fetching: boolean;
}

const INITIAL_CHAT_HISTORY_FETCH_STATE: ChatHistoryFetchState = {
  prevTokens: [],
  currentToken: '',
  nextToken: null,
  items: [],
  fetching: false,
};

const chatHistoryFetchStateHooks = makeUseGlobalClientState<ChatHistoryFetchState>(
  INITIAL_CHAT_HISTORY_FETCH_STATE
);

export const CHAT_HISTORY_QUERY = gql`
  query ChatHistoryQuery($nextToken: StrictString) @engComponent(name: LLM) @skipAudit {
    chatSessionListing(pageSize: 15, nextToken: $nextToken) {
      items {
        id
        type
        name
        initialOrigin
        modifiedAt
        star {
          id
          isStarred
        }
      }
      total
      nextToken
    }
  }
`;

function Timestamp(props: {timestamp: string}): JSX.Element {
  const formattedTime = useMemo(() => readableTime(props.timestamp), [props.timestamp]);
  return <>{formattedTime}</>;
}

function DraftIndicator({chatId}: {chatId: string | null}): JSX.Element | null {
  const [draftState] = useChatDraftState(chatId);
  const draftPreview = getDraftPreview(draftState);
  if (!draftPreview) {
    return null;
  }
  return (
    <Tooltip text={`Draft: ${draftPreview}`}>
      <span className={css(styles.draftIndicator)}>
        <BenchlingIcon icon="edit" />
      </span>
    </Tooltip>
  );
}

function ChatHistoryItemIcon({item}: {item: ChatSidepanelItem}): JSX.Element {
  const icon = <BenchlingIcon icon={isRoutineInitiatedChat(item) ? 'robot' : 'chat'} />;
  const tooltip = getInitialOriginTooltip(item);
  if (tooltip == null) {
    return icon;
  }
  return (
    <MantineTooltip label={tooltip}>
      <span className={css(styles.chatIconWrapper)}>{icon}</span>
    </MantineTooltip>
  );
}

// Keep in-sync with benchling/llm/chat_library/domain/constants.py:MAX_CHAT_NAME_LENGTH
const MAX_CHAT_NAME_LENGTH = 255;

function updateChatSessionInCache(
  apolloClient: ReturnType<typeof useApolloClient>,
  chatId: string,
  name: string,
  modifiedAt: string
): void {
  apolloClient.cache.modify({
    id: apolloClient.cache.identify({__typename: 'ChatSessionNode', id: chatId}),
    fields: {
      name: () => name,
      modifiedAt: () => modifiedAt,
    },
  });
}

function RenameChatModal(props: {chatId: string; currentName: string; onClose: () => void}): JSX.Element {
  const [name, setName] = useState(props.currentName);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  const apolloClient = useApolloClient();
  const trimmedName = name.trim();

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const {modifiedAt} = await updateChatSession(props.chatId, {name: trimmedName});
      updateChatSessionInCache(apolloClient, props.chatId, trimmedName, modifiedAt);
      props.onClose();
    } finally {
      setIsSaving(false);
    }
  }, [apolloClient, props, trimmedName]);

  return (
    <Modal
      title="Rename chat"
      onRequestClose={props.onClose}
      onRequestSubmit={handleSave}
      closeable={!isSaving}
      closeableOnBackgroundClick={!isSaving}
      medium={true}
      className={css(styles.renameChatModal)}
      buttons={[
        {text: 'Cancel', cancel: true},
        {
          text: isSaving ? 'Saving...' : 'Save',
          submit: true,
          className: css(styles.saveButton),
          disabled: !trimmedName || isSaving || trimmedName === props.currentName.trim(),
        },
      ]}
    >
      <TextInput
        size="md"
        aria-label="New chat name" /* Avoiding the label "name" to not trigger password manager autofill */
        ref={inputRef}
        maxLength={MAX_CHAT_NAME_LENGTH}
        value={name}
        disabled={isSaving}
        description={
          <>
            Current name: <em>{props.currentName}</em>
          </>
        }
        onChange={(e) => {
          setName(e.currentTarget.value);
        }}
      />
    </Modal>
  );
}

function ChatHistoryItem(props: {
  className?: string;
  item: ChatSidepanelItem;
  onStarUpdated: () => Promise<void>;
  onSelect: (chat: SidepanelChat) => void;
  isSelected: boolean;
}): JSX.Element {
  const {item} = props;
  const fuzzyDate = useFuzzyDate(item.modifiedAt);
  const isStarred = Boolean(item.star?.isStarred);
  const [isHovering, setIsHovering] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUpdatingStar, setIsUpdatingStar] = useState(false);
  const apolloClient = useApolloClient();

  const chatNameInfo = getChatNameRenderInfo(item);

  const onChatNameUpdated = useCallback(
    (message: {data: {name: string; modifiedAt: string}}) => {
      if (item.id != null) {
        updateChatSessionInCache(apolloClient, item.id, message.data.name, message.data.modifiedAt);
      }
    },
    [apolloClient, item.id]
  );

  useRealtimeChannelSubscription(
    item.id != null ? getRealtimeChannelNameForApiId(item.id) : '',
    onChatNameUpdated,
    [RealtimeMessageType.CHAT_NAME_UPDATED]
  );

  const openRenameModal = useCallback(() => {
    assert(item.id != null, 'User attempted to rename a non-persisted chat session');
    const key = `rename-chat-${item.id}`;
    LegacyModalContainer.open(
      key,
      <RenameChatModal
        chatId={item.id}
        currentName={chatNameInfo.text}
        onClose={() => {
          LegacyModalContainer.close(key);
        }}
      />
    );
  }, [item.id, chatNameInfo.text]);

  const handleClickStar = useCallback(async () => {
    if (item.id == null || isUpdatingStar) {
      return;
    }
    const nextIsStarred = !isStarred;
    const toggleStar = nextIsStarred ? starItems : unstarItems;
    setIsUpdatingStar(true);
    try {
      await toggleStar([{id: item.id as CoreObject['id']}]);
      await props.onStarUpdated();
    } finally {
      setIsUpdatingStar(false);
    }
  }, [isStarred, isUpdatingStar, item.id, props]);

  return (
    <SidepanelItem
      onMouseEnter={() => {
        setIsHovering(true);
      }}
      onMouseLeave={() => {
        setIsHovering(false);
      }}
      className={classNames(props.className, css(styles.chatHistoryItemRoot))}
      dataTestAttrs={{
        component: 'ChatHistoryItem',
        element: 'root',
        key: item.name ?? '',
        ...(props.isSelected ? {index: 'active'} : {}),
      }}
      icon={<ChatHistoryItemIcon item={item} />}
      isCompact={false}
      title={{
        text: chatNameInfo.text,
        node: <span className={css(styles.chatNameText)}>{chatNameInfo.node}</span>,
      }}
      showStar={item.id != null}
      starred={isStarred}
      handleClickStar={handleClickStar}
      inlineContentRight={
        item.id != null ? (
          <span
            className={css(
              styles.overflowMenuWrapper,
              !isHovering && !isMenuOpen && styles.overflowMenuHidden
            )}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <OverflowMenu
              buttonProps={{'aria-label': 'Chat options', className: css(styles.roundButton)}}
              isIconHorizontal={false}
              isOpen={isMenuOpen}
              menuProps={{dropLeft: true, width: MenuWidth.FIT_CONTENT}}
              onOpenChange={setIsMenuOpen}
              options={[
                {
                  icon: 'edit',
                  contents: <>Rename</>,
                  onSelect: openRenameModal,
                },
              ]}
            />
          </span>
        ) : null
      }
      subTitle={
        <>
          <span>Last modified </span>
          <Tooltip text={<Timestamp timestamp={item.modifiedAt} />}>
            <>{fuzzyDate}</>
          </Tooltip>
          <DraftIndicator chatId={item.id} />
        </>
      }
      onClick={() => {
        props.onSelect(item.id != null ? {id: item.id, type: item.type} : {id: null});
      }}
      isExpandable={false}
      isChild={false}
      active={props.isSelected}
    />
  );
}

interface ChatListingProps {
  fetchNext: (() => void) | null;
  fetchPrevious: (() => void) | null;
  onStarUpdated: () => Promise<void>;
  onSelectChatSession: (chat: SidepanelChat) => void;
  selectedChatSession: SidepanelChat | null;
}

function PresentationalChatListing(props: ChatListingProps & {items: Array<ChatSidepanelItem>}): JSX.Element {
  // Subscribe to new chat draft to determine if we should show placeholder
  const [newChatDraftState] = useChatDraftState(null);
  const hasNewChatDraft = hasMeaningfulDraft(newChatDraftState);

  // Show new chat placeholder if:
  // 1. Currently viewing a new chat (selectedChatSession.id == null), OR
  // 2. There's a meaningful draft for new chat and we're on the first page
  const placeholderItems = useMemo(() => {
    const shouldShowPlaceholder =
      (props.selectedChatSession && props.selectedChatSession.id == null && props.fetchPrevious == null) ||
      (hasNewChatDraft && props.fetchPrevious == null);

    if (!shouldShowPlaceholder) {
      return [];
    }

    return [
      {
        id: null,
        name: null,
        type: newChatDraftState.settings?.agentType
          ? getChatTypeForNewAgentChat(newChatDraftState.settings.agentType)
          : null,
        modifiedAt: new Date().toISOString(),
        star: null,
      },
    ];
  }, [props.selectedChatSession, props.fetchPrevious, hasNewChatDraft, newChatDraftState]);
  const allItems = useMemo(() => [...placeholderItems, ...props.items], [placeholderItems, props.items]);
  if (allItems.length === 0) {
    return (
      <div className={css(styles.emptyChatListing)}>
        <div className={css(styles.emptyChatListingMessage)}>
          <p className={css(styles.emptyChatListingMessageTitle)}>No chats found</p>
          <p>
            Start a new chat with the <BenchlingIcon icon="plus" /> icon above.
          </p>
        </div>
      </div>
    );
  }
  return (
    <Container size="sidepanel" dataTestAttrs={{component: 'ChatListing', element: 'root'}}>
      {props.fetchPrevious && (
        <ResultsPageLoader isPrevious={true} limit={2} setOffset={props.fetchPrevious} offset={0} />
      )}
      {allItems.map((item, idx) => {
        if (item.id == null) {
          return (
            <ChatHistoryItem
              className={idx === 0 ? css(styles.firstChatHistoryItem) : undefined}
              key="new-chat"
              item={item}
              onStarUpdated={props.onStarUpdated}
              onSelect={props.onSelectChatSession}
              isSelected={props.selectedChatSession?.id === item.id}
            />
          );
        }
        return (
          <ChatHistoryItem
            className={idx === 0 ? css(styles.firstChatHistoryItem) : undefined}
            key={item.id}
            item={item}
            onStarUpdated={props.onStarUpdated}
            onSelect={props.onSelectChatSession}
            isSelected={props.selectedChatSession?.id === item.id}
          />
        );
      })}
      {props.fetchNext && (
        <ResultsPageLoader isPrevious={false} limit={2} setOffset={props.fetchNext} offset={0} />
      )}
    </Container>
  );
}

export default function ChatListing({
  onSelectChatSession,
  selectedChatSession,
  refetchRef,
}: {
  onSelectChatSession: (chat: SidepanelChat) => void;
  selectedChatSession: SidepanelChat | null;
  refetchRef: React.MutableRefObject<() => void>;
}): JSX.Element {
  const [fetchState, setFetchState] = chatHistoryFetchStateHooks.useGlobalClientState(
    'ai-sidepanel-chat-history-list'
  );

  const result = useBenchlingQuery<ChatHistoryQuery, ChatHistoryQueryVariables>(CHAT_HISTORY_QUERY, {
    variables: {nextToken: fetchState.currentToken},
    fetchPolicy: 'network-only',
  });

  const refetchChatHistory = useCallback(async () => {
    setFetchState({...INITIAL_CHAT_HISTORY_FETCH_STATE, fetching: true});
    try {
      const refetchResult = await result.refetch({nextToken: INITIAL_CHAT_HISTORY_FETCH_STATE.currentToken});
      setFetchState({
        ...INITIAL_CHAT_HISTORY_FETCH_STATE,
        nextToken: refetchResult.data.chatSessionListing.nextToken,
        items: refetchResult.data.chatSessionListing.items,
      });
    } catch (e) {
      setFetchState({...fetchState, fetching: false});
      throw e;
    }
  }, [fetchState, result, setFetchState]);

  useEffect(() => {
    refetchRef.current = () => {
      setFetchState(INITIAL_CHAT_HISTORY_FETCH_STATE);
      result.refetch();
    };
  }, [setFetchState, refetchRef, result]);

  useEffect(() => {
    if (result.type === QueryResultType.SUCCESS) {
      // We keep track of just the item ids, to encourage the item to read directly from the cache,
      setFetchState({
        prevTokens: fetchState.prevTokens,
        currentToken: fetchState.currentToken,
        nextToken: result.data.chatSessionListing.nextToken,
        items: result.data.chatSessionListing.items,
        fetching: false,
      });
    }
  }, [result, setFetchState, fetchState.currentToken, fetchState.prevTokens]);

  const fetchNext = useMemo(() => {
    if (result.type === QueryResultType.SUCCESS && result.data.chatSessionListing.nextToken) {
      return () => {
        setFetchState({
          prevTokens: [...fetchState.prevTokens, fetchState.currentToken],
          currentToken: result.data.chatSessionListing.nextToken,
          nextToken: null,
          items: fetchState.items,
          fetching: true,
        });
      };
    } else {
      return null;
    }
  }, [result, setFetchState, fetchState]);

  const fetchPrevious = useMemo(() => {
    if (fetchState.prevTokens.length > 0) {
      return () => {
        setFetchState({
          prevTokens: fetchState.prevTokens.slice(0, -1),
          currentToken: fetchState.prevTokens[fetchState.prevTokens.length - 1],
          nextToken: null,
          items: fetchState.items,
          fetching: false,
        });
      };
    } else {
      return null;
    }
  }, [fetchState, setFetchState]);

  const sharedListProps = {
    fetchNext,
    fetchPrevious,
    onStarUpdated: refetchChatHistory,
    onSelectChatSession,
    selectedChatSession,
  } satisfies ChatListingProps;
  switch (result.type) {
    case QueryResultType.SUCCESS:
      return <PresentationalChatListing {...sharedListProps} items={result.data.chatSessionListing.items} />;
    case QueryResultType.ERROR:
      return (
        <Container size="sidepanel" className={css(styles.errorContainer)}>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text="Error loading chats."
            action={{
              actionType: InlineBannerActionType.CUSTOM,
              actionText: 'Retry',
              actionOnClick: () => {
                result.refetch();
              },
              rightAlign: true,
            }}
          />
        </Container>
      );
    case QueryResultType.PENDING:
      return (
        <div style={{opacity: 0.5}}>
          {fetchState.items.length ? (
            <PresentationalChatListing {...sharedListProps} items={fetchState.items} />
          ) : (
            <CenteredHorizontalThrobber />
          )}
        </div>
      );
    default:
      throw new Error(`Unknown result type: ${assertNever(result).type}`);
  }
}

const styles = StyleSheet.create({
  renameChatModal: {
    display: 'flex',
    alignItems: 'center',
  },
  chatHistoryItemRoot: {
    position: 'relative',
  },
  firstChatHistoryItem: {
    borderTop: `1px solid transparent`,
  },
  emptyChatListing: {
    flex: 0.3,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: '20px',
  },
  emptyChatListingMessageTitle: {
    color: Color.GRAY_600,
    fontSize: '18px',
  },
  emptyChatListingMessage: {
    color: Color.GRAY_500,
    fontSize: '14px',
    lineHeight: '20px',
  },
  errorContainer: {
    padding: '12px',
  },
  draftIndicator: {
    marginLeft: '8px',
    color: Color.GRAY_500,
    display: 'inline-flex',
    alignItems: 'center',
  },
  chatIconWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  overflowMenuWrapper: {
    position: 'absolute' as const,
    right: 32,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    alignItems: 'center',
  },
  overflowMenuHidden: {
    visibility: 'hidden' as const,
    pointerEvents: 'none' as const,
  },
  roundButton: {
    borderRadius: '50%',
  },
  saveButton: {
    minWidth: 80,
  },
  chatNameText: {
    display: 'block',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});
